import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { SUBJECT_ID_VACANCY } from 'cityads-v3/components/Forms/ContactForm';

import Page from 'cityads-v3/components/Page';
import Balaclava from 'cityads-v3/components/Balaclava';
import ContactFormPageSection from 'cityads-v3/components/Forms/ContactFormPageSection';
import { DownloadButton } from 'cityads-v3/components/Buttons';

import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab';

import {
  fetchEntity,
} from 'cityads-v3/actions/entity';

import {
  approxFormatBinaryNumber,
} from 'cityads-v3/utils/textUtils';

import classes from './Vacancy.scss';

export default function Vacancy(props) {
  const { t } = useTranslation(['About']);
  const dispatch = useDispatch();
  const language = useSelector(state => state.language.language);

  useEffect(() => {
    dispatch(fetchEntity({ api: 'jobOpportunitySection' }));
    dispatch(fetchEntity({ api: 'jobOpportunity' }));
  }, [dispatch, language]);

  const jobOpportunitySections = useSelector(state => state.entities.jobOpportunitySection);
  const jobOpportunities = useSelector(state => state.entities.jobOpportunity);

  const sortedSections = useMemo(() => {
    const isFetchingSections = jobOpportunitySections?.isFetching;
    const sections = jobOpportunitySections?.data;

    if (isFetchingSections || !sections) {
      return [];
    }

    return [...sections].sort((s1, s2) => (s1.sort - s2.sort) || (s1.id - s2.id));
  }, [jobOpportunitySections]);

  const sortedJobs = useMemo(() => {
    const isFetchingJobs = jobOpportunities?.isFetching;
    const jobs = jobOpportunities?.data;

    if (isFetchingJobs || !jobs) {
      return [];
    }

    return [...jobs].sort((s1, s2) => (s1.sort - s2.sort) || (s1.id - s2.id));
  }, [jobOpportunities]);

  const jobOpportunitiesBySectionId = useMemo(() => {
    return sortedJobs.reduce((acc, job) => ({
      ...acc,
      [job.job_opportunity_section_id]: [ ...acc[job.job_opportunity_section_id] ?? [], job ]
    }), {});
  }, [sortedJobs]);

  const [currentTab, setCurrentTab] = useState(null);

  useEffect(() => {
    setCurrentTab(sortedSections[0]?.id ?? null);
  }, [sortedSections]);

  const onTabChange = useCallback((evt, id) => {
    setCurrentTab(id);
  }, [setCurrentTab]);

  return (
    <>
      <Balaclava
        menuValue = {'vacancy'}
      />

      <Page
        className       = {classNames('vcard', classes.container)}
        title           = {t('vacancy.title')}
        subTitle        = {t('vacancy.subtitle')}
        backgroundColor = "#469ddf"
        backgroundImage = {require('./bg.jpg')}
        pageTitle       = {t('vacancy.title')}
        pageDescription = {t('vacancy.description')}
        pageKeywords    = {t('vacancy.keywords')}
      >
        <ContactFormPageSection defaultSubject={SUBJECT_ID_VACANCY} showAttach={true} />

        <Paper square elevation={0} data-as-is={true}>
          {jobOpportunitySections?.isFetching ?
            (
              <div className={classes.filesLoading}>
                <CircularProgress />
              </div>
            ) :
            sortedSections.length ?
              (
                <div>
                  <div className={classes.filesRow}>
                    <Tabs
                      indicatorColor="primary"
                      textColor="primary"
                      variant="scrollable"
                      scrollButtons="auto"
                      value={currentTab ?? sortedSections[0]?.id ?? null}
                      onChange={onTabChange}
                    >
                      {sortedSections.map(section => (
                        <Tab
                          key={section.id}
                          label={section.translated_name}
                          value={section.id}
                        />
                      ))}
                    </Tabs>
                  </div>
                  {jobOpportunities?.isFetching ?
                    (
                      <div className={classes.filesLoading}>
                        <CircularProgress />
                      </div>
                    ) :
                    (
                      <div className={classes.filesItems}>
                        {jobOpportunitiesBySectionId[currentTab]?.map(job => (
                          <DownloadButton
                            key={job.id}
                            className={classes.filesItem}
                            title={job.translated_name}
                            downloadTitle={t('vacancy.download')}
                            url={job.file?.[0]?.source}
                            size={approxFormatBinaryNumber(job.file_size)}
                            ext={job.file?.[0]?.fileName?.match(/\.[^.]*$/)?.[0]}
                          />
                        ))}
                      </div>
                    )
                  }
                </div>
              ) :
              null
          }
        </Paper>
      </Page>
    </>
  );
};
